/**
 * meters/lk4405.ts — L&T LK4405 column map (lean, 30 columns)
 * ============================================================
 * Sheets: OMEGA, TAPARIA, JD CABLES — currently in West Bengal, but this model
 * can be installed in ANY state; the column map is state-agnostic.
 *
 * Header layout (the live sheet's first row), in order:
 *   Timestamp, Watts_Total, Watts_R, Watts_Y, Watts_B, Watts_Avg,
 *   PF_Avg, PF_R, PF_Y, PF_B,
 *   VA_Total, VA_R, VA_Y, VA_B,
 *   VLL_Avg, V_RY, V_YB, V_BR, VLN_Avg, V_R, V_Y, V_B,
 *   Current_Total, Current_R, Current_Y, Current_B,
 *   Frequency_Hz, Energy_Wh, Energy_VAh, Load_Hours
 *
 * Fields the LK4405 does NOT report (VAR, harmonics, THD, K-factor, min/max,
 * phase angles, delivered registers, meter-native demand, neutral current) are
 * left at 0 and, where physically meaningful, derived in finalizeRaw():
 *   • VAR per phase + total  ← sqrt(VA² − W²)
 * Neutral current and voltage/current unbalance are computed downstream by the
 * derivedEngine from the phase currents/voltages, so they are intentionally
 * left at 0 here (the engine fills them for both models identically).
 *
 * NOTE ON ENERGY UNITS:
 *   The reference PDF labels the Energy_VAh column "kVAh". The billing engines
 *   divide cumulative-register deltas by 1000 (Wh→kWh, VAh→kVAh), matching the
 *   prior single-state WB build. We therefore store the register value as-is
 *   (energyWhScale = energyVahScale = 1) to preserve existing bill figures. If
 *   a future sheet truly stores kWh/kVAh, flip the scale to 1000 here only —
 *   no engine code changes.
 */

import type { RawMeterRow } from '@/types';
import type { MeterModel } from './types';
import { applyAliases, reactiveFromApparent } from './helpers';

export const LK4405: MeterModel = {
  code: 'lk4405',
  label: 'L&T LK4405',
  vendor: 'L&T',
  description: 'Lean HT/LT meter: active/apparent power, PF, voltages, currents, frequency '
    + 'and cumulative Wh/VAh registers. No VAR / harmonics / THD / min-max registers.',
  expectedColumns: 30,

  // "Watts_Avg" + "Energy_Wh" together (and the ABSENCE of VAR_Total) identify the LK4405.
  signatureHeaders: ['Watts_Avg', 'Energy_Wh', 'Energy_VAh', 'Current_Total'],

  fieldMap: {
    wattsTotal: 'Watts_Total', wattsR: 'Watts_R', wattsY: 'Watts_Y', wattsB: 'Watts_B',
    // Watts_Avg has no canonical slot — intentionally not mapped.
    truePFAvg: 'PF_Avg', truePFR: 'PF_R', truePFY: 'PF_Y', truePFB: 'PF_B',
    vaTotal: 'VA_Total', vaR: 'VA_R', vaY: 'VA_Y', vaB: 'VA_B',
    vllAvg: 'VLL_Avg', vRY: 'V_RY', vYB: 'V_YB', vBR: 'V_BR',
    vlnAvg: 'VLN_Avg', vR: 'V_R', vY: 'V_Y', vB: 'V_B',
    iTotal: 'Current_Total', iR: 'Current_R', iY: 'Current_Y', iB: 'Current_B',
    frequencyHz: 'Frequency_Hz',
    whReceived: 'Energy_Wh', vahReceived: 'Energy_VAh',
    loadHoursReceived: 'Load_Hours',
  },

  energyWhScale: 1,
  energyVahScale: 1,

  finalizeRaw: (r: RawMeterRow): RawMeterRow => {
    // Derive reactive power per phase + total from apparent and active power,
    // so VAR-based charts still render for an LK4405 install.
    r.varR = reactiveFromApparent(r.vaR, r.wattsR);
    r.varY = reactiveFromApparent(r.vaY, r.wattsY);
    r.varB = reactiveFromApparent(r.vaB, r.wattsB);
    r.varTotal = reactiveFromApparent(r.vaTotal, r.wattsTotal);
    return applyAliases(r);
  },
};
